import { useEffect, useRef, useState } from 'react';
import { buildDuplicateDefaultTag } from '../../ahuUtils';

interface CreateVersionPopupProps {
  /** Source AHU display tag (static_version_name || tag). */
  sourceTag: string;
  /** Confirm → POST /ahu/version/create/ {unit_id, static, tag} (B1.6). */
  onConfirm: (tag: string, isStatic: boolean) => void;
  onClose: () => void;
}

/**
 * Create-Version popup (`/static/mvc/view/version_popup.js` +
 * version_popup.tmpl, B1.6 / parity 148): heading "Create AHU Version";
 * Version Type radio toggle (`editable` default checked | `static`) with
 * `.version-mode-option.selected` labels; Source AHU; New Version AHU tag
 * input (default tag, focused + selected after 50ms, Enter confirms,
 * <3 trimmed chars → `.invalid` + title "Tag must be at least 3
 * characters."); Create Version / Cancel; Esc closes (this popup removes
 * its listener properly — matching the legacy).
 */
const CreateVersionPopup = ({ sourceTag, onConfirm, onClose }: CreateVersionPopupProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [tag, setTag] = useState(() => buildDuplicateDefaultTag(sourceTag));
  const [mode, setMode] = useState<'editable' | 'static'>('editable');
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.which === 27) onClose();
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [onClose]);

  const confirm = () => {
    const trimmed = (tag || '').trim();
    if (trimmed.length < 3) {
      setInvalid(true);
      return;
    }
    onConfirm(trimmed, mode === 'static');
    onClose();
  };

  return (
    <div className="popup-wrapper width-medium active">
      <div className="popup-content version-popup">
        <h2 className="popup-heading">Create AHU Version</h2>
        <div className="popup-content-part col-xs-12">
          <div className="row version-popup-row version-popup-top-margin">
            <div className="col-xs-12">
              <div className="col-xs-3 version-popup-label">
                <p className="name">Version Type</p>
              </div>
              <div className="col-xs-9">
                <div
                  className="version-mode-toggle"
                  role="radiogroup"
                  aria-label="Version mode"
                >
                  <label
                    className={`version-mode-option${
                      mode === 'editable' ? ' selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="version_mode"
                      value="editable"
                      checked={mode === 'editable'}
                      onChange={() => setMode('editable')}
                    />
                    <span>Editable</span>
                  </label>
                  <label
                    className={`version-mode-option${
                      mode === 'static' ? ' selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="version_mode"
                      value="static"
                      checked={mode === 'static'}
                      onChange={() => setMode('static')}
                    />
                    <span>Static</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="row version-popup-header">
            <div className="col-xs-12">
              <div className="col-xs-3 version-popup-label">
                <p className="name">Source AHU</p>
              </div>
              <div className="col-xs-9">
                <span className="name">{sourceTag}</span>
              </div>
            </div>
          </div>

          <div className="row version-popup-row">
            <div className="col-xs-12">
              <div className="col-xs-3 version-popup-label">
                <p className="name">New Version AHU</p>
              </div>
              <div className="col-xs-9">
                <input
                  ref={inputRef}
                  type="text"
                  className={`version-tag-input${invalid ? ' invalid' : ''}`}
                  name="version_tag"
                  value={tag}
                  maxLength={30}
                  autoComplete="off"
                  placeholder="Enter a custom tag or accept the default"
                  title={invalid ? 'Tag must be at least 3 characters.' : undefined}
                  onChange={(e) => {
                    setTag(e.target.value);
                    if (invalid && e.target.value.trim().length >= 3) setInvalid(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.which === 13) {
                      e.preventDefault();
                      confirm();
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="popup-control-buttons" style={{ paddingTop: 20 }}>
          <button
            className="btn right highlighted-btn"
            name="confirm_version"
            onClick={confirm}
          >
            Create Version
          </button>
          <button
            className="btn right btn_cancel not-highlighted-btn"
            id="btn_cancel"
            name="cancel_version"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateVersionPopup;
